import { useGlobalContext } from "../context/GlobalComponentsContext";
import { useEffect, useMemo } from "react";
import { reportError } from "../utils/errorUtils";
import { GetFamilyMemberCareTeamMatchesQuery, useGetFamilyMemberCareTeamMatchesQuery } from "src/queries/gqlTypes";
import { AccountType } from "../model/userModels";

type CareTeamMemberMatch = GetFamilyMemberCareTeamMatchesQuery["careTeamMemberMatches"][number];
type MatchedCareTeamMember = CareTeamMemberMatch["careTeamMember"];

export default function useCareTeamMemberMatches(userIds: number[], options?: { filterByCareProviders?: boolean }) {
  const { showError } = useGlobalContext();

  const { data, loading, error } = useGetFamilyMemberCareTeamMatchesQuery({
    fetchPolicy: "cache-and-network",
    variables: { userIds: userIds },
    skip: !userIds.length
  });

  useEffect(() => {
    if (error) {
      reportError(error);
      showError();
    }
  }, [error, showError]);

  const careTeamMemberMatches: CareTeamMemberMatch[] = useMemo(() => {
    const matches = data?.careTeamMemberMatches || [];
    if (options?.filterByCareProviders) {
      return matches.filter(m => AccountType.isCareProvider(m.careTeamMember.accountType));
    }
    return matches;
  }, [data, options?.filterByCareProviders]);

  const careTeamMembers: MatchedCareTeamMember[] = useMemo(() => {
    return careTeamMemberMatches.map(m => m.careTeamMember);
  }, [careTeamMemberMatches]);

  const familyMembers = useMemo(() => data?.familyMembers || [], [data]);

  return { data, loading, error, careTeamMemberMatches, careTeamMembers, familyMembers };
}
